import {isNil, isNotNil} from 'ramda';

import {Nullish} from 'shared/web';

import {FeatureSubCategory} from '../types/FeatureSubCategory';
import {FeatureSubCategoryGroup} from '../types/FeatureSubCategoryGroup';

const OTHER_GROUP_KEY = 'other';

type GroupFeaturesBySubCategoryParams<T> = {
  items: T[];
  categoryKey: string;
  /**
   * Subcategories in the order defined by the catalogue.
   */
  subCategories: FeatureSubCategory[];
  getSubCategory: (item: T) => string | Nullish;
};

/**
 * Splits features of one category into groups by their catalogue subcategory.
 *
 * - groups keep the catalogue order of subcategories, items keep their incoming order
 * - a subcategory is used only when it belongs to the given category, otherwise the item falls into the "other" group
 * - the "other" group is always last
 * - when no item has a subcategory, a single "other" group is returned, so callers can render the category without headings
 */
export function groupFeaturesBySubCategory<T>(
  params: GroupFeaturesBySubCategoryParams<T>
): FeatureSubCategoryGroup<T>[] {
  const categorySubCategories = params.subCategories.filter(
    (subCategory) => subCategory.parent === params.categoryKey
  );
  const subCategoryKeys = new Set(categorySubCategories.map((subCategory) => subCategory.key));

  const resolveKey = (item: T) => {
    const subCategory = params.getSubCategory(item);
    return isNotNil(subCategory) && subCategoryKeys.has(subCategory) ? subCategory : null;
  };

  const subCategoryGroups = categorySubCategories
    .map((subCategory) => ({
      key: subCategory.key,
      label: subCategory.label,
      isOther: false,
      items: params.items.filter((item) => resolveKey(item) === subCategory.key),
    }))
    .filter((group) => group.items.length > 0);

  const otherItems = params.items.filter((item) => isNil(resolveKey(item)));
  const otherGroup =
    otherItems.length > 0
      ? [{key: OTHER_GROUP_KEY, label: null, isOther: true, items: otherItems}]
      : [];

  return [...subCategoryGroups, ...otherGroup];
}
